// API functions for call routing
import { getGenericDeviceConfiguration, updateCallRoutingConfiguration } from '@/lib/api'
import type { CallRoutingConfiguration } from './types'

/**
 * Fetches the current call routing configuration from the API
 * 
 * The API returns response.data.resultObject which is already a string
 * that needs to be parsed as JSON. This matches the old Vue frontend behavior.
 */
export const fetchCallRoutingConfiguration = async (): Promise<CallRoutingConfiguration> => {
    try {
        const response = await getGenericDeviceConfiguration()

        // The response is the resultObject directly (a string that needs to be parsed)
        // This matches the old Vue frontend: JSON.parse(response)
        if (response && typeof response === 'string') {
            try {
                const parsedConfig = JSON.parse(response)
                return {
                    selectionElements: parsedConfig.selectionElements || []
                }
            } catch (parseError) {
                console.error('Error parsing call routing configuration string:', parseError)
                return { selectionElements: [] }
            }
        }

        // Handle case where response might already be an object (fallback)
        if (response && typeof response === 'object') {
            // Check if it has errorCode (error case)
            if ((response as any).errorCode) {
                console.error('API returned error:', (response as any).errorCode)
                return { selectionElements: [] }
            }

            // If it has selectionElements directly, use it
            if ((response as any).selectionElements) {
                return {
                    selectionElements: (response as any).selectionElements || []
                }
            }
        }

        console.warn('Unexpected response format from getGenericDeviceConfiguration:', response)
        return { selectionElements: [] }
    } catch (error) {
        console.error('Error fetching call routing configuration:', error)
        return { selectionElements: [] }
    }
}

/**
 * Saves the call routing configuration to the API
 */
export const saveCallRoutingConfiguration = async (configuration: CallRoutingConfiguration): Promise<boolean> => {
    try {
        const configString = JSON.stringify(configuration)
        const response = await updateCallRoutingConfiguration(configString)

        // Check if response has errorCode property (error case) or is successful
        return !(response as any).errorCode
    } catch (error) {
        console.error('Error saving call routing configuration:', error)
        return false
    }
}